import { DataService } from '../utilities/data.service';
import { Consent } from '../../dataTypes/type-consent';
import { UcErrorMessage } from '../../Enums/uc-errors.enum';
const SHA256 = require('sha256');

/**
 * Service respomnsible for fetching consents in different occasions:
 */
export class GetConsentsService {

    /**
     * Id of the optin template.
     */
    static optinTemplateId = 'HJI5SmLm7';

    static deprepactedOptinTemplateIds = ['SJZvUaJVQ', 'H1giW5JV7', 'rkVxVS1NQ', 'ryveNSJEX',
        'ByKH19C7X', 'BygwgNHJNQ', 'ByuxVB1Em', 'H18xEr1Em', 'HJde4r14Q', 'HyceEByVQ'];

    /**
     * return category object using categorySlug
     * @param categorySlug
     */
    static getAppropriateCategory(categories: Consent.Category[], categorySlug: string): Consent.Category {
        return categories.find((category) => { return category.categorySlug === categorySlug; });
    }

    /**
     * Finds the Category this consent belongs to or undefined if this consent is not part of a category
     * @param selectedConsent 
     */
    static getCategoryByConsent(selectedConsent: Consent.Consent): Consent.Category {
        return DataService.categories.find((cat: Consent.Category) => {

            // return the consent that matches the templateId
            const consent = cat.consents.find(consent => {
                return selectedConsent.templateId === consent.templateId;
            });
            return typeof consent !== 'undefined';
        });
    }

    /**
     * return consent object using templateId
     * @param templateId
     */
    static getAppropriateConsent(templateId: string): Consent.Consent {
        return DataService.consents.find((consent) => { return consent.templateId === templateId });
    }

    /**
     * return consent object using processorname
     * @param processorname
     */
    static getConsentByProcessor(processorname: string): Consent.Consent {

        // Loop over all consentTemplates
        for (const templateId in DataService.consentTemplates) {

            // Loop over all versions (normally there should only be one)
            for (const version in DataService.consentTemplates[templateId]) {

                // If it matches, return the appropriate consent
                if (DataService.consentTemplates[templateId][version].dataProcessors[0] === processorname) {
                    return GetConsentsService.getAppropriateConsent(templateId);
                }
            }
        }
        // If nothing is found, return undefined
        return undefined;
    }

    static getConsentByIabId(iabId: number): Consent.Consent {

        for (const templateId in DataService.consentTemplates) {
            for (const version in DataService.consentTemplates[templateId]) {
                if (Number(DataService.consentTemplates[templateId][version].iabId) === Number(iabId)) {
                    return GetConsentsService.getAppropriateConsent(templateId);
                }
            }
        }
        // If nothing is found, return undefined
        return undefined;
    }

    /**
     * get appropriate template using templateId and version
     * @param templateId
     * @param version
     */
    static getAppropriateTemplate(templateId: string, version: string): Consent.ConsentTemplate {
        //@TODO: check if templateId and version exist
        return DataService.consentTemplates[templateId] && DataService.consentTemplates[templateId][version]
            ? DataService.consentTemplates[templateId][version]
            : undefined;
    }

    /**
     * getOptin consents from list of consents
     */
    static getOptinConsent(): Consent.Consent {
        const allOptinConsents = GetConsentsService.deprepactedOptinTemplateIds.concat([GetConsentsService.optinTemplateId]);
        const optinConsent = DataService.consents.find(c => allOptinConsents.indexOf(c.templateId) > -1);
        if (!optinConsent) {
            throw UcErrorMessage.OPTIN_CONSENT_MISSING;
        }
        return optinConsent;
    }

    /**
     * Checks whether or not this consent is essential. Only consents
     * with a parent category can be essential, because only categories can be essential.
     * @param consent 
     */
    static isConsentEssential(consent: Consent.Consent): boolean {

        const parentCategory = GetConsentsService.getCategoryByConsent(consent);

        // can not set essential consent to false
        if (typeof parentCategory !== 'undefined' && parentCategory.isEssential) {
            return true;
        }

        return false;
    }

    /**
     * Gets the consent id as a hash based on settingsId, controllerId and processorId.
     * @param consent 
     */
    static getConsentId(consent: Consent.Consent): string {
        if (!DataService.settings) {
            throw 'getConsentId could not find setting';
        }
        return SHA256(`${DataService.settings.settingsId}_${JSON.stringify([consent.controllerId])}_${JSON.stringify([consent.processorId])}`);
    }
}
